import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  BasketballOperationsCenterConflict,
  BasketballOperationsCenterConflictSeverityEnum,
  BasketballOperationsCenterGame,
} from '@hefe/models';
import { formatDate, fullUserName } from '@hefe/utils';
import { List, Space, Tag, Typography } from 'antd';
import { useMemo } from 'react';

interface ConflictItemComponentProps {
  conflict: BasketballOperationsCenterConflict;
  games: { [key: number]: BasketballOperationsCenterGame };
}

export default function ConflictItemComponent(
  props: ConflictItemComponentProps
) {
  const { conflict } = props;

  const conflictGames = useMemo(() => {
    return conflict.game_ids.map((id) => props.games[id]);
  }, [conflict.game_ids, props.games]);

  const conflictSeverity = useMemo(() => {
    switch (conflict.severity) {
      case BasketballOperationsCenterConflictSeverityEnum.ORANGE:
        return <WarningOutlined style={{ color: 'orange' }} />;
      case BasketballOperationsCenterConflictSeverityEnum.RED:
        return <ExclamationCircleOutlined style={{ color: 'red' }} />;
      default:
        return null;
    }
  }, [conflict]);

  const conflictRoleTags = useMemo(() => {
    return conflict.roles.map((role, index) => (
      <Tag key={index} color="blue">
        {t(`operations_center.conflict.roles.${role}`)}
      </Tag>
    ));
  }, [conflict.roles]);

  const date = useMemo(() => {
    if (conflictGames.length === 0) {
      return null;
    }
    // Just take the date of the first game for now
    return formatDate(conflictGames[0].date);
  }, [conflictGames]);

  return (
    <List.Item>
      <List.Item.Meta
        title={
          <Space>
            {conflictSeverity}
            <Typography.Text>
              {t('operations_center.conflict.messages.overlapping', {
                name: fullUserName(conflict?.user),
              })}
            </Typography.Text>
          </Space>
        }
        description={
          <Space direction="vertical">
            <Space size={4}>{conflictRoleTags}</Space>
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {conflictGames
                .map((game) => game?.home + ' - ' + game?.guest)
                .join(' | ')}
            </Typography.Text>
          </Space>
        }
      />
      <Typography.Text type="secondary">{date}</Typography.Text>
    </List.Item>
  );
}
